import { Component, computed, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { BaseModalComponent } from 'src/app/core/base-modal.componant';
import { SessionService } from 'src/app/shared/services/session.service';
import { LeagueIterationService } from 'src/app/simulator-booking/services/league-iteration.service';
import { LeagueIteration } from 'src/app/simulator-booking/models/league-iteration';
import { provideNativeDateAdapter } from '@angular/material/core';
import * as moment from 'moment-timezone';
import { BookingService } from 'src/app/simulator-booking/services/booking.service';
import { ScheduledBookingTypeEnum } from 'src/app/shared/models/scheduled-booking-type.enum';

@Component({
    selector: 'edit-league-iteration-modal',
    templateUrl: './edit-league-iteration-modal.component.html',
    styleUrl: './edit-league-iteration-modal.component.scss',
    providers: [provideNativeDateAdapter()],
    standalone: false
})
export class EditLeagueIterationModalComponent extends BaseModalComponent implements OnInit {
    public userTimezone: string;
    public iteration: LeagueIteration;
    public form = new FormGroup({
        id: new FormControl('', {
            validators: Validators.required
        }),
        year: new FormControl('', {
            validators: Validators.required
        }),
        startDateOnly: new FormControl('', {
            validators: Validators.required
        }),
        endDateOnly: new FormControl('', {
            validators: Validators.required
        }),
        startTimeOnly: new FormControl(null, {
            validators: Validators.required
        }),
        endTimeOnly: new FormControl<Date | null>(null, {
            validators: Validators.required
        }),
        entryFee: new FormControl(0, {
            validators: [Validators.required, Validators.min(0)]
        }),
        stripePriceId: new FormControl(''),
        notes: new FormControl(''),
    });
    public bookings = signal<any[]>([]);
    public displayedColumns: string[] = ['selected', 'simulator', 'date', 'timeSlot', 'confirmationNumber'];

    public allSelected = computed(() =>
        this.bookings().length > 0 &&
        this.bookings().every(b => b.selected)
    );

    public partiallySelected = computed(() =>
        this.bookings().some(b => b.selected) && !this.allSelected()
    );

    constructor(
        private sessionService: SessionService,
        private leagueIterationService: LeagueIterationService,
        private bookingService: BookingService,
        public dialogRef: MatDialogRef<EditLeagueIterationModalComponent>
    ) {
        super();
        this.userTimezone = this.sessionService.getUser().timeZone;
    }

    ngOnInit(): void {
        if (this.iteration) {
            const startDateOnly = new Date(this.formatInUserTimezone(this.iteration.startDate, 'MM/DD/YYYY')).toISOString();
            const endDateOnly = new Date(this.formatInUserTimezone(this.iteration.endDate, 'MM/DD/YYYY')).toISOString();

            const startTimeOnly = new Date();
            startTimeOnly.setHours(new Date(this.formatInUserTimezone(this.iteration.startDate, 'MM/DD/YYYY hh:mm A')).getHours(), new Date(this.formatInUserTimezone(this.iteration.startDate, 'MM/DD/YYYY hh:mm A')).getMinutes());
            const endTimeOnly = new Date();
            endTimeOnly.setHours(new Date(this.formatInUserTimezone(this.iteration.endDate, 'MM/DD/YYYY hh:mm A')).getHours(), new Date(this.formatInUserTimezone(this.iteration.endDate, 'MM/DD/YYYY hh:mm A')).getMinutes());

            this.form.patchValue({
                id: this.iteration.id,
                year: this.iteration.year.toString(),
                startDateOnly: startDateOnly,
                endDateOnly: endDateOnly,
                startTimeOnly: startTimeOnly,
                endTimeOnly: endTimeOnly,
                entryFee: this.iteration.entryFee,
                stripePriceId: this.iteration.stripePriceId,
                notes: this.iteration.notes
            });

            this.getBookings();
        }
    }

    getBookings(): void {
        this.isLoading = true;
        this.bookingService.getReferenceBookings({
            referenceId: this.iteration.id,
            type: ScheduledBookingTypeEnum.League,
        }).subscribe({
            next: (result) => {
                this.bookings.set(result.map(booking => ({ ...booking, selected: moment.utc(booking.startTime).tz(this.userTimezone).isBefore(moment.utc().tz(this.userTimezone)) ? false : true })) || []);
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error getting bookings:', error);
                this.isLoading = false;
            }
        });
    }


    toggleAllSelections(checked: boolean): void {
        const updated = this.bookings().map(b => ({
            ...b,
            selected: moment.utc(b.startTime).tz(this.userTimezone).isBefore(moment.utc().tz(this.userTimezone)) ? false : checked
        }));
        this.bookings.set(updated);
    }

    toggleSelection(booking: any): void {
        const updated = this.bookings().map(b =>
            b === booking ? { ...b, selected: !b.selected } : b
        );
        this.bookings.set(updated);
    }

    isBookingPast(booking: any): boolean {
        if (moment.utc(booking.startTime).tz(this.userTimezone).isBefore(moment.utc().tz(this.userTimezone))) {
            booking.selected = false;
            return true;
        }
        return false;
    }

    isSelectedAllDisabled(): boolean {
        return this.bookings().every(b => this.isBookingPast(b));
    }

    submit({ value, valid }): void {
        if (valid) {
            this.isLoading = true;
            const startDate = new Date(value.startDateOnly);
            const endDate = new Date(value.endDateOnly);

            const data = {
                ...value,
                startDateOnly: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
                endDateOnly: `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`,
                startTimeOnly: this.formatTimeOnly(value.startTimeOnly),
                endTimeOnly: this.formatTimeOnly(value.endTimeOnly),
                organizationId: this.iteration.organizationId,
                lastEditedBy: this.sessionService.getUserId(),
                timeZone: this.sessionService.getUser().timeZone,
                leagueId: this.iteration.leagueId,
                bookingIds: this.bookings().filter(b => b.selected).map(b => b.bookingId) || []
            };

            this.leagueIterationService.update(this.iteration.id, data).subscribe({
                next: (result) => {
                    if (result !== null && result !== undefined) {
                        this.dialogRef.close("save");
                    }
                },
                error: (error) => {
                    console.error('Error updating iteration:', error);
                    this.setErrorV2(error, this.form);
                    this.isLoading = false;
                },
                complete: () => this.isLoading = false
            });
        }
    }

    formatInUserTimezone(date: Date, format: string): string {
        return moment.utc(date).tz(this.userTimezone).format(format);
    }

    private formatTimeOnly(value: any): string {
        // Accept Date, time string (HH:mm or HH:mm:ss), or ISO/string parseable by Date
        if (value instanceof Date) {
          const hh = String(value.getHours()).padStart(2, '0');
          const mm = String(value.getMinutes()).padStart(2, '0');
          const ss = String(value.getSeconds()).padStart(2, '0');
          return `${hh}:${mm}:${ss}`;
        }
    
        if (typeof value === 'string') {
          const timeMatch = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
          if (timeMatch) {
            const hh = String(Math.min(23, parseInt(timeMatch[1], 10))).padStart(2, '0');
            const mm = String(Math.min(59, parseInt(timeMatch[2], 10))).padStart(2, '0');
            const ss = String(Math.min(59, timeMatch[3] ? parseInt(timeMatch[3], 10) : 0)).padStart(2, '0');
            return `${hh}:${mm}:${ss}`;
          }
          const asDate = new Date(value);
          if (!isNaN(asDate.getTime())) {
            const hh = String(asDate.getHours()).padStart(2, '0');
            const mm = String(asDate.getMinutes()).padStart(2, '0');
            const ss = String(asDate.getSeconds()).padStart(2, '0');
            return `${hh}:${mm}:${ss}`;
          }
        }
        
        // Unknown structure (e.g., null), default to 00:00:00
        return '00:00:00';
      }
}

